// WelcomeTemplate — the first frame visitors see on the landing.
// Larger orb (the breathing presenter), single warm hello, tap-to-begin
// affordance. Minimal — the orb is the star here.

import { motion } from 'framer-motion';
import { Stage } from '../stage/Stage';
import { Orb } from '../stage/Orb';
import { Eyebrow } from '../stage/Eyebrow';
import { fadeUp, stagger } from '../motion/variants';
import type { SceneContent } from '../types';

interface WelcomeProps {
  content: SceneContent;
  onBegin?: () => void;
}

export function WelcomeTemplate({ content, onBegin }: WelcomeProps) {
  const { headline, body, eyebrow } = content;
  return (
    <Stage>
      <motion.div
        initial="hidden"
        animate="show"
        exit="exit"
        variants={stagger(0.18)}
        className="m-auto max-w-[960px] w-full flex flex-col items-center text-center gap-12"
      >
        {/* Orb — large, central, the focal point */}
        <motion.div variants={fadeUp}>
          <Orb size={160} />
        </motion.div>

        {eyebrow && (
          <motion.div variants={fadeUp}>
            <Eyebrow>{eyebrow}</Eyebrow>
          </motion.div>
        )}

        <motion.h1
          variants={fadeUp}
          className="text-display-2 font-display max-w-[20ch] text-balance text-fg"
        >
          {headline ?? "Hi. I'm Data."}
        </motion.h1>

        {body && (
          <motion.p variants={fadeUp} className="text-subhead text-fg-soft max-w-[42ch]">
            {body}
          </motion.p>
        )}

        {onBegin && (
          <motion.button
            variants={fadeUp}
            onClick={onBegin}
            className="mt-4 px-7 py-4 rounded-full bg-accent text-void-900 font-display font-semibold text-body hover:bg-accent-glow transition-colors"
          >
            Tap to begin →
          </motion.button>
        )}

        <motion.span
          variants={fadeUp}
          className="text-caption font-mono text-fg-mute uppercase tracking-[0.18em] mt-2"
        >
          Microphone + audio
        </motion.span>
      </motion.div>
    </Stage>
  );
}
